import React, { useState } from 'react';
import { T, font, mono } from '../utils/theme';
import * as api from '../utils/api';
import { Card, Sect, Btn, Inp, Sel, SearchBar, StatusBadge, SBadge, ViewToggle, Modal, IcEdit, IcCheck, useIsMobile, matchesSearch } from './UI';
import { money } from '../utils/money';

export default function AdminOrdering({ lang, products, suppliers, allSuppliers, reload, Lk }) {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [reassignModal, setReassignModal] = useState(null);
  const [reassignTo, setReassignTo] = useState('');
  const [reassignQty, setReassignQty] = useState('');
  const [editQtyId, setEditQtyId] = useState(null);
  const [editQtyVal, setEditQtyVal] = useState('');
  const [restockModal, setRestockModal] = useState(null);
  const [restockQty, setRestockQty] = useState('');
  const [hoveredId, setHoveredId] = useState(null);

  const assignedP = products.filter(p => p.status === 'supplier_assigned');
  const orderingP = products.filter(p => p.status === 'ordering');
  const allOrdering = [...assignedP, ...orderingP];

  const matchFilter = (p) => {
    if (!search) return true;
    const sup = allSuppliers.find(s => s.id === p.assigned_supplier);
    return matchesSearch(search, p.name) || matchesSearch(search, p.sku || '') || matchesSearch(search, sup?.name || '');
  };

  const filtered = allOrdering.filter(matchFilter);

  const doReassign = async () => {
    if (!reassignModal || !reassignTo) return;
    const qty = reassignQty ? parseInt(reassignQty) : (products.find(p => p.id === reassignModal)?.assigned_qty || 0);
    await api.assignProduct(reassignModal, reassignTo, qty);
    setReassignModal(null);
    setReassignTo('');
    setReassignQty('');
    await reload();
  };

  const doSaveQty = async (id) => {
    if (!editQtyVal && editQtyVal !== '0') return;
    await api.updateProduct(id, { assigned_qty: parseInt(editQtyVal) || 0 });
    setEditQtyId(null);
    setEditQtyVal('');
    await reload();
  };

  const openRestockModal = (p) => {
    setRestockModal(p);
    setRestockQty(String(p.assigned_qty || p.qty_ordered || ''));
  };

  const doConfirmRestock = async () => {
    if (!restockModal) return;
    await api.confirmReceipt(restockModal.id, parseInt(restockQty) || 0);
    setRestockModal(null);
    await reload();
  };

  const openReassign = (p) => {
    setReassignModal(p.id);
    setReassignTo('');
    setReassignQty(String(p.assigned_qty || p.qty_ordered || ''));
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 22px', fontSize: 22, fontWeight: 900, color: T.text }}>{Lk('ordering')} ({allOrdering.length})</h2>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', position: 'sticky', top: 0, zIndex: 20, background: T.bg, paddingTop: 4, paddingBottom: 8 }}>
        <SearchBar value={search} onChange={setSearch} placeholder={`${Lk('searchProduct')} / ${Lk('searchSku')} / ${Lk('searchSupplier')}`} style={{ flex: 1 }} />
        <ViewToggle mode={viewMode} setMode={setViewMode} lang={lang} />
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic', padding: 20 }}>{search ? Lk('noResults') : (lang === 'zh' ? '目前没有在订购中的产品。' : 'No products in the ordering pipeline.')}</div>
      ) : (!isMobile && viewMode === 'table') ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
            <thead>
              <tr>
                {[Lk('productName'), Lk('sku'), Lk('cost'), Lk('qty'), Lk('status'), Lk('supplier'), Lk('dateChanged'), Lk('actions')].map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '8px 12px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const sup = allSuppliers.find(s => s.id === p.assigned_supplier);
                const q = p.quotes?.[p.assigned_supplier];
                const isEditingQty = editQtyId === p.id;
                return (
                  <tr key={p.id} style={{ background: hoveredId === p.id ? '#EFF6FF' : 'transparent' }} onMouseEnter={() => setHoveredId(p.id)} onMouseLeave={() => setHoveredId(null)}>
                    <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontWeight: 600, color: T.text }}>{p.name}</td>
                    <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, fontSize: 11, color: T.muted }}>{p.sku}</td>
                    <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: '#fff' }}>{q ? `${money(q.price)}` : '—'}</td>
                    <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}>
                      {isEditingQty ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input type="number" value={editQtyVal} onChange={e => setEditQtyVal(e.target.value)} autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') doSaveQty(p.id); if (e.key === 'Escape') setEditQtyId(null); }}
                            style={{ fontFamily: mono, fontSize: 12, padding: '4px 6px', width: 60, background: T.bg, border: `1px solid ${T.accent}`, borderRadius: 4, color: T.text, outline: 'none' }} />
                          <Btn variant="small" style={{ padding: '3px 6px', fontSize: 10 }} onClick={() => doSaveQty(p.id)}>{IcCheck}</Btn>
                        </div>
                      ) : (
                        <span onClick={() => { setEditQtyId(p.id); setEditQtyVal(String(p.assigned_qty || p.qty_ordered || '')); }}
                          style={{ fontFamily: mono, cursor: 'pointer', borderBottom: `1px dashed ${T.dim}`, paddingBottom: 1, color: '#fff' }}
                          title={lang === 'zh' ? '点击编辑数量' : 'Click to edit qty'}>
                          {p.assigned_qty || p.qty_ordered || '—'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}><StatusBadge status={p.status} lang={lang} /></td>
                    <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, color: T.muted }}>{sup?.name || '—'}</td>
                    <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontSize: 11, color: T.dim, fontFamily: mono }}>{p.acknowledged_at || p.untick_date || '—'}</td>
                    <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Btn variant="ghostSm" onClick={() => openReassign(p)}>{IcEdit} {Lk('reassign')}</Btn>
                        {p.status === 'ordering' && <Btn variant="successSm" onClick={() => openRestockModal(p)}>{Lk('markRestocked')}</Btn>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          {assignedP.filter(matchFilter).length > 0 && (
            <Sect title={lang === 'zh' ? '已分配 — 等待确认' : 'Assigned — Awaiting Confirmation'}>
              {assignedP.filter(matchFilter).map(p => {
                const sup = allSuppliers.find(s => s.id === p.assigned_supplier);
                const q = p.quotes?.[p.assigned_supplier];
                return (
                  <Card key={p.id} hoverBg style={{ marginBottom: 8, borderColor: T.accent + '40' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>{p.name}</span>
                        <span style={{ fontSize: 12, color: T.muted, marginLeft: 10 }}>→ {sup?.name}</span>
                        {q && <span style={{ fontFamily: mono, fontSize: 12, color: '#fff', marginLeft: 10 }}>{money(q.price)}</span>}
                        <span style={{ fontFamily: mono, fontSize: 12, color: '#fff', marginLeft: 10 }}>× {p.assigned_qty || p.qty_ordered || '—'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <StatusBadge status="supplier_assigned" lang={lang} />
                        <Btn variant="ghostSm" onClick={() => openReassign(p)}>{IcEdit} {Lk('reassign')}</Btn>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </Sect>
          )}
          {orderingP.filter(matchFilter).length > 0 && (
            <Sect title={lang === 'zh' ? '订购中 — 已确认' : 'Ordering — Confirmed'}>
              {orderingP.filter(matchFilter).map(p => {
                const sup = allSuppliers.find(s => s.id === p.assigned_supplier);
                const q = p.quotes?.[p.assigned_supplier];
                return (
                  <Card key={p.id} hoverBg style={{ marginBottom: 8, borderColor: T.purple + '40' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>{p.name}</span>
                        <span style={{ fontSize: 12, color: T.muted, marginLeft: 10 }}>→ {sup?.name}</span>
                        {q && <span style={{ fontFamily: mono, fontSize: 12, color: '#fff', marginLeft: 10 }}>{money(q.price)}</span>}
                        <span style={{ fontFamily: mono, fontSize: 12, color: '#fff', marginLeft: 10 }}>× {p.assigned_qty || p.qty_ordered || '—'}</span>
                        {p.acknowledged_at && <span style={{ fontSize: 11, color: T.dim, marginLeft: 10 }}>{Lk('confirmed')} {p.acknowledged_at}</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <StatusBadge status="ordering" lang={lang} />
                        <Btn variant="ghostSm" onClick={() => openReassign(p)}>{IcEdit} {Lk('reassign')}</Btn>
                        <Btn variant="successSm" onClick={() => openRestockModal(p)}>{Lk('markRestocked')}</Btn>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </Sect>
          )}
        </>
      )}

      {/* Reassign modal with qty */}
      <Modal open={reassignModal !== null} onClose={() => setReassignModal(null)}>
        {(() => {
          const p = products.find(x => x.id === reassignModal);
          const hasQuote = (sid) => { const q = p?.quotes?.[sid]; return !!q && !!q.available && q.price != null; };
          const noQuote = !!reassignTo && !hasQuote(reassignTo);
          return (
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 4 }}>{Lk('reassign')}</div>
              {p && <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>{p.name}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Sel label={lang === 'zh' ? '选择新供应商' : 'Select new supplier'} value={reassignTo} onChange={e => setReassignTo(e.target.value)}>
                  <option value="">{lang === 'zh' ? '选择...' : 'Choose...'}</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}{hasQuote(s.id) ? '' : (lang === 'zh' ? '（未报价）' : ' (no quote yet)')}</option>)}
                </Sel>
                {noQuote && (
                  <div style={{ fontSize: 12, color: T.warn, lineHeight: 1.5 }}>
                    {lang === 'zh'
                      ? '该供应商尚未报价。此产品将回到报价中，仅限该供应商报价。收到报价后，在报价页面分配。'
                      : 'This supplier has not quoted. The item goes back to Quoting, open to this supplier only. Assign it from Quoting once their quote is in.'}
                  </div>
                )}
                <Inp label={Lk('qty')} type="number" value={reassignQty} onChange={e => setReassignQty(e.target.value)} placeholder="Quantity" />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <Btn variant="secondary" onClick={() => setReassignModal(null)} style={{ flex: 1 }}>{Lk('cancel')}</Btn>
                <Btn onClick={doReassign} style={{ flex: 1 }}>{noQuote ? (lang === 'zh' ? '发送报价' : 'Send to quoting') : Lk('assign')}</Btn>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Mark Restocked confirmation modal */}
      <Modal open={restockModal !== null} onClose={() => setRestockModal(null)}>
        {restockModal && (() => {
          const sup = allSuppliers.find(s => s.id === restockModal.assigned_supplier);
          return (
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 4 }}>
                {lang === 'zh' ? '确认收货' : 'Confirm Receipt'}
              </div>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 4 }}>{restockModal.name}</div>
              {sup && <div style={{ fontSize: 12, color: T.dim, marginBottom: 16 }}>{lang === 'zh' ? '供应商：' : 'Supplier: '}<span style={{ color: T.text, fontWeight: 600 }}>{sup.name}</span></div>}
              <Inp
                label={lang === 'zh' ? '收到数量' : 'Qty Received'}
                type="number"
                value={restockQty}
                onChange={e => setRestockQty(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') doConfirmRestock(); }}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <Btn variant="secondary" onClick={() => setRestockModal(null)} style={{ flex: 1 }}>{Lk('cancel')}</Btn>
                <Btn variant="success" onClick={doConfirmRestock} style={{ flex: 1 }}>
                  {IcCheck} {lang === 'zh' ? '确认收货' : 'Confirm Received'}
                </Btn>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
